import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CountryChoice, countryLabel } from "@/components/CountryPrompt";

export default function Settings() {
  const { user, logout, isViewAs, privacy } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const [editCountry, setEditCountry] = useState(false);

  // The password-change control is ONLY available when actually authenticated
  // as the backup admin account. Never for Google-signed-in users, and never
  // while View As is active (which is always read-only).
  const canChangePassword = !isViewAs && (user?.email || "").toLowerCase() === "admin@atl.com";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setCur(""); setNw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto space-y-5" data-testid="settings-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Account</div>
        <h1 className="font-serif text-3xl text-[#0A1F44] mt-1">Settings</h1>
      </div>

      <Link to={`/people/${user?.id}`} data-testid="settings-my-profile"
            className="block rounded-xl border border-[#C9A961]/40 bg-white p-4 text-sm text-[#0A1F44] hover:border-[#C9A961]">
        <span className="font-medium">My partner profile</span>
        <span className="block text-xs text-slate-500">Business milestones, Bootcamp progress and onboarding history</span>
      </Link>

      <Card className="p-5 space-y-2">
        <div className="text-sm">
          <div className="text-slate-500 text-xs uppercase tracking-wider">Name</div>
          <div className="text-slate-900 font-medium">{user?.name}</div>
        </div>
        <div className="text-sm pt-2">
          <div className="text-slate-500 text-xs uppercase tracking-wider">Email</div>
          <div className="text-slate-900">{user?.email}</div>
        </div>
        <div className="text-sm pt-2">
          <div className="text-slate-500 text-xs uppercase tracking-wider">Role · Team · Upline</div>
          <div className="text-slate-900">
            {user?.role}{user?.upline_team ? ` · ${user.upline_team}` : ""}
          </div>
        </div>
        <div className="text-sm pt-2" data-testid="settings-country">
          <div className="text-slate-500 text-xs uppercase tracking-wider">Country</div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-slate-900" data-testid="settings-country-value">{countryLabel(user)}</span>
            {!isViewAs && <button type="button" onClick={() => setEditCountry((o) => !o)} data-testid="settings-country-edit"
              className="text-xs text-[#8A6D1F] underline underline-offset-2">{editCountry ? "Cancel" : "Change"}</button>}
          </div>
          {editCountry && !isViewAs && <div className="pt-2"><CountryChoice prefix="settings" onSaved={() => setEditCountry(false)} /></div>}
        </div>
        {!canChangePassword && (
          <div className="text-xs text-slate-500 pt-3 border-t border-slate-100 mt-2">
            You're signed in with Google. There's no password on this account — use “Continue with Google” each time.
          </div>
        )}
      </Card>

      <Card className="p-5 space-y-2 text-sm" data-testid="settings-privacy-card">
        <h2 className="font-serif text-lg text-[#0A1F44]">Privacy</h2>
        <Link to="/privacy" data-testid="settings-privacy-link" className="text-[#0A1F44] underline underline-offset-2">Read the Team Smith Hub Privacy Notice</Link>
        {!isViewAs && privacy && (
          <div className="text-xs text-slate-500" data-testid="settings-privacy-status">
            {privacy.privacy_notice_at
              ? `You acknowledged version ${privacy.acknowledged_version} on ${new Date(privacy.privacy_notice_at).toLocaleString("en-AU", { timeZone: "Australia/Melbourne" })} (Melbourne).`
              : "No acknowledgement on file yet."}
          </div>
        )}
      </Card>

      {canChangePassword && (
        <Card className="p-5" data-testid="change-password-card">
          <h2 className="font-serif text-lg text-[#0A1F44] mb-3">Change password</h2>
          <p className="text-xs text-slate-500 mb-3">Backup Admin login only.</p>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Current password</Label>
              <Input data-testid="cur-pw" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required />
            </div>
            <div>
              <Label>New password</Label>
              <Input data-testid="new-pw" type="password" value={nw} onChange={(e) => setNw(e.target.value)} required minLength={6} />
            </div>
            <Button data-testid="save-pw" disabled={busy} type="submit" className="bg-[#0A1F44] text-white rounded-full">Update password</Button>
          </form>
        </Card>
      )}

      <Button data-testid="settings-logout" variant="outline" onClick={logout} className="rounded-full">Log out</Button>
    </div>
  );
}
